import { ActionIcon, Badge, Box, Divider, Flex, Stack, Text } from '@mantine/core';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  IconBuildingSkyscraper,
  IconChevronLeft,
  IconDeviceMobile,
  IconGripVertical,
  IconPencil,
  IconPhone,
} from '@tabler/icons-react';
import Loader from 'components/Loader';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CALL_ORDER_PAGE_PATH, HOUSE_PAGE_PATH, SET_PHONE_PAGE_PATH, SET_SECOND_PHONE_PAGE_PATH } from 'routes/paths';
import theme from 'theme';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import P2Medium from 'theme/components/Typography/P2Medium';
import { useApi } from 'api/api-context';
import panic from 'panic';
import { GetCallOrderResponse } from 'api/actions/get-call-order/get-call-order-response';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P2Regular from 'theme/components/Typography/P2Regular';

type Channel = 'sip' | 'indoor' | 'gsm' | 'gsm2';

/**
 * Returns label and subtitle for a channel.
 */
const getChannelInfo = (
  channel: Channel,
  _t: (key: string) => string,
  data: GetCallOrderResponse | null
): { label: string; subtitle: string } => {
  switch (channel) {
    case 'sip':
      return { label: _t('Phillip App'), subtitle: _t('Ring in the Phillip app') };
    case 'indoor':
      return { label: _t('Indoor unit'), subtitle: _t('Indoor intercom in your apartment') };
    case 'gsm':
      return { label: _t('Primary number'), subtitle: data?.phoneNumber ? data.phoneNumber : _t('Not set') };
    case 'gsm2':
      return {
        label: _t('Additional number'),
        subtitle: data?.secondPhoneNumber ? data.secondPhoneNumber : _t('Not set'),
      };
  }
};

/**
 * Returns the icon component for a channel.
 */
const ChannelIcon: React.FC<{ channel: Channel; color: string }> = ({ channel, color }) => {
  switch (channel) {
    case 'sip':
      return <IconDeviceMobile size="24px" color={color} />;
    case 'indoor':
      return <IconBuildingSkyscraper size="24px" color={color} />;
    case 'gsm':
    case 'gsm2':
      return <IconPhone size="24px" color={color} />;
  }
};

/**
 * Builds the full channel order (all channels).
 * Preserves the user's stored order and appends any new channels at the end.
 * Unconfigured channels (gsm without phoneNumber, gsm2 without secondPhoneNumber)
 * are always placed at the end — gsm before gsm2.
 */
export const buildDisplayOrder = (data: GetCallOrderResponse): Channel[] => {
  const stored = Array.isArray(data.callOrder) ? (data.callOrder as Channel[]) : [];

  // Configured channels first, unconfigured at the end.
  const available: Channel[] = [];
  if (data.hasSip) available.push('sip');
  if (data.indoorUnitNumber) available.push('indoor');
  if (data.phoneNumber) available.push('gsm');
  if (data.secondPhoneNumber) available.push('gsm2');
  if (!data.phoneNumber) available.push('gsm');
  if (!data.secondPhoneNumber) available.push('gsm2');

  const ordered = stored.filter((ch) => available.includes(ch));
  available.filter((ch) => !ordered.includes(ch)).forEach((ch) => ordered.push(ch));

  // Always enforce: unconfigured channels stay at the end.
  const unconfigured: Channel[] = [];
  if (!data.phoneNumber) unconfigured.push('gsm');
  if (!data.secondPhoneNumber) unconfigured.push('gsm2');
  if (unconfigured.length > 0) {
    return [...ordered.filter((ch) => !unconfigured.includes(ch)), ...unconfigured];
  }
  return ordered;
};

interface SortableChannelItemProps {
  /** Channel identifier used as the dnd-kit unique id. */
  id: Channel;
  /** 1-based position shown outside the card. */
  index: number;
  /** Resolved display label. */
  label: string;
  /** Resolved subtitle (phone number, indoor number, …). */
  subtitle: string;
  /** Whether this channel has an edit action (gsm / gsm2). */
  isPhone: boolean;
  /** False for gsm2 when no secondary phone is set — disables drag and grays out the card. */
  isDraggable: boolean;
  /** Called when the pencil icon / "Nastaviť" text is pressed. */
  onEdit: () => void;
}

/**
 * A single sortable row: position badge (outside) + draggable card.
 * Drag listeners are attached only to the grip icon so clicks on
 * the pencil button never accidentally start a drag.
 * When isDraggable is false the card is grayed out and drag is disabled.
 */
const SortableChannelItem: React.FC<SortableChannelItemProps> = ({
  id,
  index,
  label,
  subtitle,
  isPhone,
  isDraggable,
  onEdit,
}) => {
  const { _t } = useLang();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !isDraggable,
  });

  const GRAY_INACTIVE = '#A9B0BC';

  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    flex: 1,
    borderRadius: 10,
    background: 'white',
    userSelect: 'none' as const,
  };

  const iconColor = isDraggable ? theme.colors?.gray?.[12] ?? '#000' : GRAY_INACTIVE;
  const gripColor = isDraggable ? theme.colors?.gray?.[7] ?? '#000' : GRAY_INACTIVE;

  return (
    <Flex align="center" gap="0.75rem" data-testid={`call-order-${id}`}>
      {/* Position badge — outside the card, stays static during drag */}
      <Badge
        size="lg"
        variant="filled"
        color="white"
        style={{
          minWidth: 24,
          height: 24,
          padding: 0,
          borderRadius: '50%',
          fontSize: 14,
          flexShrink: 0,
          color: isDraggable ? theme.colors?.gray?.[12] : GRAY_INACTIVE,
        }}
      >
        {index + 1}
      </Badge>

      {/* Draggable card */}
      <Flex ref={setNodeRef} align="center" gap="0.75rem" p="16px" style={cardStyle}>
        {/* Label + subtitle */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Flex gap="8px" align="center" mb="4px">
            <ChannelIcon channel={id} color={iconColor} />
            <H2SemiBold truncate c={isDraggable ? undefined : GRAY_INACTIVE}>
              {label}
            </H2SemiBold>
          </Flex>

          {subtitle ? (
            <P2Regular c={isDraggable ? 'dimmed' : GRAY_INACTIVE} truncate>
              {subtitle}
            </P2Regular>
          ) : null}
        </Box>

        {/* Right action: pencil (configured) or "Nastaviť" text (gsm2 not configured) */}
        {isPhone && (
          <>
            {isDraggable ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="md"
                aria-label={`${label}: ${_t('Set')}`}
                data-testid={`call-order-edit-${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <IconPencil size="24px" color={theme.colors?.gray?.[7]} />
              </ActionIcon>
            ) : (
              <Text
                style={{
                  color: 'var(--Primary, #551FFF)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                  fontSize: 14,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                {_t('Set')}
              </Text>
            )}
          </>
        )}

        {/* Vertical divider */}
        <Divider
          orientation="vertical"
          style={{ height: '2.5rem', alignSelf: 'center', color: theme.colors?.gray?.[2] }}
        />

        {/* Grip handle — the ONLY element that starts a drag */}
        <Box
          {...attributes}
          {...(isDraggable ? listeners : {})}
          role="button"
          aria-label={`${label}: ${_t('Call order')}`}
          data-testid={`call-order-handle-${id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
            touchAction: 'none',
          }}
        >
          <IconGripVertical size="24px" color={gripColor} />
        </Box>
      </Flex>
    </Flex>
  );
};

/**
 * Call Order page — drag-and-drop interface for managing the call sequence.
 * Uses @dnd-kit for touch + mouse support. Auto-saves on drop.
 */
const CallOrder: React.FC = () => {
  const { _t } = useLang();
  const { getAction } = useApi();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<GetCallOrderResponse | null>(null);
  const [order, setOrder] = useState<Channel[]>([]);

  /**
   * Sensors: PointerSensor (mouse/stylus) + TouchSensor (mobile) + KeyboardSensor.
   * activationConstraint prevents accidental drags when tapping buttons.
   */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /**
   * Load call order data from API.
   */
  useEffect(() => {
    const getCallOrder = getAction('GetCallOrder');
    getCallOrder({})
      .then((response) => {
        setData(response);
        setOrder(buildDisplayOrder(response));
        setLoading(false);
      })
      .catch(panic);
  }, [getAction]);

  /**
   * Auto-save the call order. Excludes channels that have no phone number set.
   */
  const saveOrder = useCallback(
    (newOrder: Channel[], currentData: GetCallOrderResponse | null) => {
      const updateCallOrder = getAction('UpdateCallOrder');
      const toSave = newOrder.filter((ch) => {
        if (ch === 'gsm' && !currentData?.phoneNumber) return false;
        if (ch === 'gsm2' && !currentData?.secondPhoneNumber) return false;
        return true;
      });
      if (toSave.length === 0) return;
      updateCallOrder({ payload: { callOrder: toSave as [Channel] } }).catch(panic);
    },
    [getAction]
  );

  /**
   * Handle drag end — reorder and auto-save.
   * Channels without a phone number stay pinned at the end (gsm before gsm2).
   */
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = order.indexOf(active.id as Channel);
      const newIndex = order.indexOf(over.id as Channel);
      let newOrder = arrayMove(order, oldIndex, newIndex);

      // Keep channels without a number pinned at the end: gsm (if unset) then gsm2 (if unset).
      const pinned: Channel[] = [];
      if (!data?.phoneNumber) pinned.push('gsm');
      if (!data?.secondPhoneNumber) pinned.push('gsm2');
      if (pinned.length > 0) {
        newOrder = [...newOrder.filter((ch) => !pinned.includes(ch)), ...pinned];
      }

      setOrder(newOrder);
      saveOrder(newOrder, data);
    },
    [order, data, saveOrder]
  );

  /**
   * Navigate to the set-phone page (edit primary GSM).
   */
  const handleGsmClick = useCallback(() => {
    navigate(SET_PHONE_PAGE_PATH.original);
  }, [navigate]);

  /**
   * Navigate to the set-second-phone page (edit or add secondary GSM).
   */
  const handleGsm2Click = useCallback(() => {
    navigate(SET_SECOND_PHONE_PAGE_PATH.original, { state: { returnTo: CALL_ORDER_PAGE_PATH.original } });
  }, [navigate]);

  return (
    <SimpleLayout>
      <Box p="1rem">
        {/* Header */}
        <Box mb="1rem">
          <Flex>
            <Link
              to={HOUSE_PAGE_PATH.original}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}
            >
              <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
              <P2Medium c={theme.colors?.gray?.[9]}>{_t('Back to my household')}</P2Medium>
            </Link>
          </Flex>
          <Flex gap="0.5rem" align="center">
            <IconPhone size="2rem" color={theme.colors?.gray?.[12]} />
            <H1SemiBold c={theme.colors?.gray?.[12]}>{_t('Call order')}</H1SemiBold>
          </Flex>
          <Text size="sm" c="dimmed" mt="0.25rem">
            {_t('Set the order in which devices will be contacted when someone rings your doorbell.')}
          </Text>
        </Box>

        {loading ? (
          <Loader />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={order} strategy={verticalListSortingStrategy}>
              <Stack gap="0.5rem">
                {order.map((channel, index) => {
                  const { label, subtitle } = getChannelInfo(channel, _t, data);
                  const isPhone = channel === 'gsm' || channel === 'gsm2';
                  const isDraggable =
                    channel === 'gsm'
                      ? Boolean(data?.phoneNumber)
                      : channel === 'gsm2'
                        ? Boolean(data?.secondPhoneNumber)
                        : true;
                  const onEdit = channel === 'gsm' ? handleGsmClick : handleGsm2Click;
                  return (
                    <SortableChannelItem
                      key={channel}
                      id={channel}
                      index={index}
                      label={label}
                      subtitle={subtitle}
                      isPhone={isPhone}
                      isDraggable={isDraggable}
                      onEdit={onEdit}
                    />
                  );
                })}
              </Stack>
            </SortableContext>
          </DndContext>
        )}
      </Box>
    </SimpleLayout>
  );
};

export default CallOrder;
